






















































import { Component, Vue, Watch, Ref } from "vue-property-decorator";
import { observePanelsOfClient } from "@/data/repository/PanelRepository";
import { observeComBoxesOfClient } from "@/data/repository/ComBoxRepository";

import { getIresDeviceCategory, IresDevice, IresDeviceCategory } from "@/data/models/IresDevice";
import { ComBox } from "@/data/models/ComBox";
import { Panel } from "@/data/models/Panel";

import { getDefaultBranchFilterOfUser } from "@/data/repository/UserRepository";

import {
  HyDryEventMessagesFilter,
  HyDryMessageFilterType,
} from "@/utils/HyDryEventMessagesFilter";
import { observeHyDryDeviceEventMessages } from "@/data/repository/HyDryEventsRepository";
import {
  getHyDryEventCategoryDescription,
  getHyDryPanelEventNumber,
  HyDryEventCategory,
  HyDryPanelEvent,
  LatestHyDryEventMessageInfo,
  UnreadHyDryEventMessagesInfo,
} from "@/data/models/HyDryEvent";
import HyDryEventMessageItemComponent from "@/components/messages/HyDryEventMessageItem.vue";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import MenuIconWithSelectionListComponent from "../common/FilterMenu.vue";
import router from "@/router/router";
import {
  findBranch,
  hasClientEnabledStrictBranchFilters,
} from "@/data/repository/ClientRepository";
import { BranchFilter } from "@/data/models/BranchFilter";
import { UserModule } from "@/store/modules/UserModule";
import { observeMailbox } from "@/data/repository/MailboxRepository";
import { Mailbox } from "@/data/models/Mailbox";
import { MailboxFilter } from "@/utils/MailboxFilter";
import { UserLookup } from "@/data/models/UserLookup";
import { calculateVisibleHyDryEventCategories } from "@/data/models/HyDryMessagesConfig";
import MultiSelectionDialogComponent from "@/components/common/MultiSelectionDialog.vue";
import MenuIconWithMultiSelectionListComponent from "../common/MultiSelectionFilterMenu.vue";
import { allHyDryDevicesAccess, officeAccess, projectManagementAccess } from "@/permissions/UserPermissions";
import { getHyDryMessagesSettings } from "@/data/repository/SettingsRepository";
import { COLOR_SELECTED_NON_DEFAULT_TOOLBAR_FILTER_BACKGROUND } from "@/utils/colors";
import { getEffectiveUser } from "@/utils/ViewAs";

interface HyDryEventMessagesData {
  iresDevice: IresDevice;
  messages: UnreadHyDryEventMessagesInfo[];
}

enum HyDryEventMessageSortOrder {
  MESSAGE_DATE,
  PROJECT_NUMBER,
  SERIAL_NUMBER,
}

@Component({
  name: "MessageListComponent",
  components: {
    "hy-dry-event-message-item": HyDryEventMessageItemComponent,
    "menu-icon": MenuIconWithSelectionListComponent,
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "multi-selection-dialog": MultiSelectionDialogComponent,
  },
})
export default class MessageListComponent extends Vue {
  @Ref("multiSelectionDialog")
  multiSelectionDialog!: MultiSelectionDialogComponent<HyDryEventCategory>;

  // The user whose messages these are: the chosen one while "Sicht auf" is active, on every path
  // into the list, "all messages" included (ticket 2610052). Scaffold re-creates the page when it changes.
  targetUser = getEffectiveUser();

  subscriptions: Array<() => void> = [];
  panels: Array<Panel> = [];
  comBoxes: Array<ComBox> = [];

  selectedSerialNumber = "";

  componentActive = false;

  iresDeviceLookup = new Map<string, IresDevice>();

  allHyDryEventMessages: LatestHyDryEventMessageInfo[] = [];
  hyDryEventMessagesToDisplay: HyDryEventMessagesData[] = [];

  panelUpdateDebouncer: NodeJS.Timeout | null = null;
  comBoxUpdateDebouncer: NodeJS.Timeout | null = null;

  filterType = HyDryMessageFilterType.ALL_MESSAGES;

  selectableHyDryEventCategories = [
    HyDryEventCategory.ORGANISATION,
    HyDryEventCategory.DRYING_PROGRESS,
    HyDryEventCategory.ALERT,
    HyDryEventCategory.OTHER,
  ];

  selectedHyDryEventCategories: HyDryEventCategory[] = [];
  defaultVisibleHyDryEventCategories: HyDryEventCategory[] = [];

  selectedBranchFilter = getDefaultBranchFilterOfUser();
  hyDryEventMessagesFilter = HyDryEventMessagesFilter.createFilterFromSettings(
    this.targetUser,
    this.selectedBranchFilter,
    [],
    this.hyDryMessagesSettings
  );

  userLookup = new UserLookup(UserModule.activeUsersOfClient);

  mailboxes: Mailbox[] = [];
  mailboxFilter = new MailboxFilter(
    this.mailboxes,
    this.userLookup,
    this.selectedBranchFilter,
    [],
    this.hyDryMessagesSettings
  );

  selectedSortOrder = HyDryEventMessageSortOrder.MESSAGE_DATE;
  allSortOrders = [
    HyDryEventMessageSortOrder.MESSAGE_DATE,
    HyDryEventMessageSortOrder.PROJECT_NUMBER,
    HyDryEventMessageSortOrder.SERIAL_NUMBER,
  ];

  isLoading = false;

  @Watch("selectedSortOrder")
  onSelectedSortOrderChanged() {
    this.calculateHyDryMessagesToDisplay();
  }

  @Watch("selectedHyDryEventCategories")
  onSelectedHyDryEventCategoriesChanged() {
    this.updateFilter();
  }

  get hyDryMessagesSettings() {
    return getHyDryMessagesSettings();
  }

  get sortOrderIcon() {
    return "mdi-sort-alphabetical-variant";
  }

  get filterIcon() {
    return "mdi-filter-variant";
  }

  get filterStyle() {
    const filter = this.hyDryEventMessagesFilter;

    const defaultVisibleCategories = calculateVisibleHyDryEventCategories(
      this.filterType,
      filter.targetUser,
      this.hyDryMessagesSettings
    );

    const currentlyVisibleCategories = filter.getVisibleHyDryEventCategories(
      this.filterType
    );

    const isShowingDefaultCategories =
      defaultVisibleCategories.size === currentlyVisibleCategories.size &&
      [...defaultVisibleCategories.values()].every((category) =>
        currentlyVisibleCategories.has(category)
      );

    return isShowingDefaultCategories
      ? ""
      : `background-color: ${COLOR_SELECTED_NON_DEFAULT_TOOLBAR_FILTER_BACKGROUND}`;
  }

  getHyDryEventCategoryName(hyDryEventCategory: HyDryEventCategory): string {
    return getHyDryEventCategoryDescription(this, hyDryEventCategory, true);
  }

  getSortOrderName(sortOrder: HyDryEventMessageSortOrder): string {
    switch (sortOrder) {
      case HyDryEventMessageSortOrder.MESSAGE_DATE:
        return this.$t("messages.sortBy.messageDate").toString();
      case HyDryEventMessageSortOrder.PROJECT_NUMBER:
        return this.$t("messages.sortBy.projectNumber").toString();
      case HyDryEventMessageSortOrder.SERIAL_NUMBER:
        return this.$t("messages.sortBy.serialNumber").toString();
    }
  }

  calculateHyDryMessagesToDisplay() {
    const result: HyDryEventMessagesData[] = [];
    let messages: LatestHyDryEventMessageInfo[] = [];

    if (this.filterType === HyDryMessageFilterType.MAILBOX) {
      messages =
        this.mailboxFilter.getMessagesInAllMailboxes(
          this.allHyDryEventMessages
        )[0]?.messages ?? [];
    } else {
      const filter = this.hyDryEventMessagesFilter;
      messages = filter.getFilteredMessageForFilterType(
        this.allHyDryEventMessages,
        this.filterType
      );
    }

    messages.forEach((messagesOfDevice) => {
      const newestMessage = messagesOfDevice.unreadMessages[0].latestMessage;
      const serialNumber = newestMessage.serialNumber;
      const iresDevice = this.iresDeviceLookup.get(serialNumber);

      if (!iresDevice) {
        return;
      }

      const isPanel = getIresDeviceCategory(iresDevice) === IresDeviceCategory.PANEL;
      if (isPanel) {
        const logsStartingFrom = new Date("2023/08/05");
        const filteredMessages = messagesOfDevice.unreadMessages.filter((message) => {

          if (!message || !message.latestMessage) {
            return [];
          }

          return message.latestMessage.messageDate >= logsStartingFrom && message.latestMessage.eventNumber !== getHyDryPanelEventNumber(HyDryPanelEvent.STEP)
        });

        if (filteredMessages.length > 0) {
          result.push({
            iresDevice,
            messages: filteredMessages,
          });
        }
      }
      else {
        result.push({
          iresDevice,
          messages: messagesOfDevice.unreadMessages,
        });
      }
    });

    const sortOrder = this.selectedSortOrder;

    this.hyDryEventMessagesToDisplay = result.sort((a, b) => {
      switch (sortOrder) {
        case HyDryEventMessageSortOrder.MESSAGE_DATE: {
          //Already sorted by date
          return 0;
        }
        case HyDryEventMessageSortOrder.PROJECT_NUMBER: {
          const isAssignedToProjectA = !!a.iresDevice.currentAssignment
            ?.currentProjectId;
          const isAssignedToProjectB = !!b.iresDevice.currentAssignment
            ?.currentProjectId;

          if (isAssignedToProjectA && isAssignedToProjectB) {
            const currentProjectNumberA =
              a.iresDevice.currentAssignment?.currentProjectNumber ?? "";
            const currentProjectNumberB =
              b.iresDevice.currentAssignment?.currentProjectNumber ?? "";

            return currentProjectNumberA.localeCompare(currentProjectNumberB);
          } else if (!isAssignedToProjectA && !isAssignedToProjectB) {
            return a.iresDevice.serialNo.localeCompare(b.iresDevice.serialNo);
          } else {
            return isAssignedToProjectA ? 1 : -1;
          }
        }
        case HyDryEventMessageSortOrder.SERIAL_NUMBER: {
          return a.iresDevice.serialNo.localeCompare(b.iresDevice.serialNo);
        }
      }
    });
  }

  updateFilter() {
    const messageFilter = HyDryEventMessagesFilter.createFilterFromSettings(
      this.targetUser,
      this.selectedBranchFilter,
      [...this.panels, ...this.comBoxes],
      this.hyDryMessagesSettings
    );

    const mailboxFilter = new MailboxFilter(
      this.mailboxes,
      this.userLookup,
      this.selectedBranchFilter,
      [...this.panels, ...this.comBoxes],
      this.hyDryMessagesSettings
    );

    messageFilter.updateVisibleHyDryEventCategories(
      this.filterType,
      this.selectedHyDryEventCategories
    );

    mailboxFilter.updateVisibleHyDryEventCategories(
      this.selectedHyDryEventCategories
    );

    this.hyDryEventMessagesFilter = messageFilter;
    this.mailboxFilter = mailboxFilter;

    this.calculateHyDryMessagesToDisplay();
  }

  async created() {
    this.isLoading = true;
    const showAllMessages = router.currentRoute.query.showAllMessages;

    if (showAllMessages) {
      this.filterType = HyDryMessageFilterType.ALL_MESSAGES;
      this.defaultVisibleHyDryEventCategories = this.selectableHyDryEventCategories;
      this.selectedHyDryEventCategories = this.selectableHyDryEventCategories;
    } else {
      const targetBranchId = router.currentRoute.query.targetBranchId as string;

      if (targetBranchId) {
        const matchingBranch = findBranch(targetBranchId);

        if (matchingBranch) {
          this.selectedBranchFilter = new BranchFilter(
            hasClientEnabledStrictBranchFilters(),
            matchingBranch
          );
        }
      }

      const filterTypeQuery = router.currentRoute.query.filterType as string;

      if (filterTypeQuery) {
        this.filterType = filterTypeQuery as HyDryMessageFilterType;

        this.defaultVisibleHyDryEventCategories = [
          ...calculateVisibleHyDryEventCategories(
            this.filterType,
            this.targetUser,
            this.hyDryMessagesSettings
          ).values(),
        ];

        this.selectedHyDryEventCategories = this.defaultVisibleHyDryEventCategories;

        if (this.filterType === HyDryMessageFilterType.MAILBOX) {
          const mailboxId = router.currentRoute.query.mailboxId as string;

          if (mailboxId) {
            this.subscriptions.push(
              observeMailbox(mailboxId, (mailbox: Mailbox) => {
                this.mailboxes = [mailbox];
              })
            );
          }
        }
      }
    }

    this.updateFilter();

    this.startObservingIresDevicesOfUser();
    this.startObservingHyDryDeviceEventMessages();

    setTimeout(() => {
      this.isLoading = false;
    }, 500);
  }

  startObservingHyDryDeviceEventMessages() {
    this.subscriptions.push(
      observeHyDryDeviceEventMessages((messageCollection) => {
        this.allHyDryEventMessages = messageCollection.latestHyDryEventMessages;
        this.calculateHyDryMessagesToDisplay();
      })
    );
  }

  get showAllIresDevicesAndProjects() {
    return allHyDryDevicesAccess(this.targetUser);
  }

  startObservingIresDevicesOfUser() {
    const userId = this.showAllIresDevicesAndProjects
      ? null
      : this.targetUser?.id;

    this.subscriptions.push(
      observePanelsOfClient(userId, (panels: Array<Panel>) => {
        this.debouncePanelUpdate(panels);
      })
    );

    this.subscriptions.push(
      observeComBoxesOfClient(userId, (comBoxes: Array<ComBox>) => {
        this.debounceComBoxUpdate(comBoxes);
      })
    );
  }

  debouncePanelUpdate(panels: Array<Panel>) {
    this.clearPanelUpdateDebouncer();

    this.panelUpdateDebouncer = setTimeout(() => {
      this.panels = panels;

      panels.forEach((panel) => {
        this.iresDeviceLookup.set(panel.serialNo, panel);
      });

      this.updateFilter();
    }, 100);
  }

  debounceComBoxUpdate(comBoxes: Array<ComBox>) {
    this.clearComBoxUpdateDebouncer();

    this.comBoxUpdateDebouncer = setTimeout(() => {
      this.comBoxes = comBoxes;

      comBoxes.forEach((comBox) => {
        this.iresDeviceLookup.set(comBox.serialNo, comBox);
      });

      this.updateFilter();
    }, 100);
  }

  clearPanelUpdateDebouncer() {
    if (this.panelUpdateDebouncer) {
      clearTimeout(this.panelUpdateDebouncer);
    }
  }

  clearComBoxUpdateDebouncer() {
    if (this.comBoxUpdateDebouncer) {
      clearTimeout(this.comBoxUpdateDebouncer);
    }
  }

  beforeDestroy() {
    this.subscriptions.forEach((subscription) => subscription());
    this.clearComBoxUpdateDebouncer();
    this.clearPanelUpdateDebouncer();
  }

  updateTitle() {
    let title = "";

    switch (this.filterType) {
      case HyDryMessageFilterType.MY_MESSAGES:
        title = this.$t("messages.newMessagesForMe").toString();
        break;
      case HyDryMessageFilterType.MAILBOX: {
        const mailboxName = router.currentRoute.query.mailboxName as string;
        title = this.$t("messages.newMessagesForMailbox", [
          mailboxName,
        ]).toString();
        break;
      }
      case HyDryMessageFilterType.MESSAGES_FOR_USER_CAPABILITY: {
        const hasOffice = officeAccess(this.targetUser);
        const hasProjectManagement = projectManagementAccess(this.targetUser);

        if (hasOffice && hasProjectManagement) {
          const capabilities = 
            this.$t("users.role.office").toString() +
            " " +
            this.$t("common.and").toString() +
            " " +
            this.$t("users.role.projectManager").toString();

          title = this.$t("messages.newMessagesForCapability", [capabilities]).toString();
        }
        else if (hasOffice) {
          title = this.$t("messages.newMessagesForCapability", [
            this.$t("users.role.office").toString()
          ]).toString();
        }
        else if (hasProjectManagement) {
          title = this.$t("messages.newMessagesForCapability", [
            this.$t("users.role.projectManager").toString()
          ]).toString();
        }

        break;
      }
      case HyDryMessageFilterType.ALL_MESSAGES:
        title = this.$t("messages.newMessages").toString();
        break;
    }

    ToolbarContentModule.setToolbarTitle(
      this.$t("messages.formatMessagesTitle", [title]).toString()
    );
  }

  activated() {
    this.updateTitle();
    this.componentActive = true;
  }

  deactivated() {
    this.componentActive = false;
  }

  mounted() {
    this.updateTitle();
    this.componentActive = true;
  }
}
